import { useEffect, useState, useCallback } from "react";
import {
  fetchContentOptions,
  fetchCurriculumContent,
  addCurriculumContent,
  deleteCurriculumContent,
  reorderCurriculumContent,
} from "./adminCourseService";
import { fetchContentBlocks } from "../content/adminContentService";

const TYPE_OPTIONS = ["GRAMMAR", "READING", "GD", "CONTENT_BLOCK"];

const TYPE_BADGE = {
  GRAMMAR:       { label: "Grammar",       bg: "#ede9fe", color: "#6d28d9" },
  READING:       { label: "Reading",       bg: "#dbeafe", color: "#1d4ed8" },
  GD:            { label: "GD",            bg: "#dcfce7", color: "#15803d" },
  CONTENT_BLOCK: { label: "Content Block", bg: "#fef9c3", color: "#a16207" },
};

/* ── Add form ── */
const AddMaterialForm = ({ curriculumId, onSaved, onCancel }) => {
  const [contentType, setContentType] = useState("");
  const [topics, setTopics]           = useState([]);
  const [topicId, setTopicId]         = useState("");
  const [children, setChildren]       = useState([]);
  const [contentId, setContentId]     = useState("");
  const [label, setLabel]             = useState("");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const isFlat = contentType === "GD" || contentType === "CONTENT_BLOCK";

  // Load topics when type changes
  useEffect(() => {
    if (!contentType) { setTopics([]); setTopicId(""); setChildren([]); setContentId(""); return; }
    if (contentType === "CONTENT_BLOCK") {
      fetchContentBlocks({ size: 200 })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data?.content ?? [];
          setTopics(list.map((b) => ({ id: b.id, title: b.title })));
          setTopicId(""); setChildren([]); setContentId("");
        })
        .catch(() => setTopics([]));
      return;
    }
    fetchContentOptions(contentType)
      .then((res) => { setTopics(res.data || []); setTopicId(""); setChildren([]); setContentId(""); })
      .catch(() => setTopics([]));
  }, [contentType]);

  // Load children when topic changes (only for hierarchical types)
  useEffect(() => {
    if (isFlat || !topicId) { setChildren([]); setContentId(""); return; }
    const topic = topics.find((t) => String(t.id) === String(topicId));
    setChildren(topic?.children || []);
    setContentId("");
  }, [topicId, topics, isFlat]);

  const handleSave = async () => {
    const finalId = isFlat ? topicId : contentId;
    if (!contentType || !finalId) { setError("Please select a type and topic."); return; }
    setSaving(true); setError("");
    try {
      await addCurriculumContent(curriculumId, {
        contentType,
        contentId: Number(finalId),
        label: label.trim() || null,
        sortOrder: 0,
      });
      onSaved();
    } catch (err) {
      setError(String(err?.response?.data?.message || err?.response?.data || "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "1rem", marginTop: "0.75rem" }}>
      <div style={{ fontWeight: 700, fontSize: "0.88rem", marginBottom: "0.75rem", color: "#0369a1" }}>Add Study Material</div>

      {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.4rem 0.6rem", borderRadius: 4, marginBottom: "0.6rem", fontSize: "0.82rem" }}>{error}</div>}

      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-end" }}>
        {/* Type */}
        <div className="ac-form-group" style={{ margin: 0, minWidth: 130 }}>
          <label style={{ fontSize: "0.78rem" }}>Content Type</label>
          <select value={contentType} onChange={(e) => setContentType(e.target.value)}>
            <option value="">— Select —</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t === "CONTENT_BLOCK" ? "Content Block" : t.charAt(0) + t.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Topic / Category — hidden for flat types like GD */}
        {!isFlat && (
          <div className="ac-form-group" style={{ margin: 0, minWidth: 160 }}>
            <label style={{ fontSize: "0.78rem" }}>Topic / Category</label>
            <select value={topicId} onChange={(e) => setTopicId(e.target.value)} disabled={!topics.length}>
              <option value="">— Select —</option>
              {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </div>
        )}

        {/* Sub-topic / Passage (hierarchical) OR GD/Content Block (flat) */}
        <div className="ac-form-group" style={{ margin: 0, minWidth: 180 }}>
          <label style={{ fontSize: "0.78rem" }}>
            {contentType === "CONTENT_BLOCK" ? "Content Block" : isFlat ? "GD Topic" : "Sub-topic / Passage"}
          </label>
          <select
            value={isFlat ? topicId : contentId}
            onChange={(e) => isFlat ? setTopicId(e.target.value) : setContentId(e.target.value)}
            disabled={isFlat ? !topics.length : !children.length}
          >
            <option value="">— Select —</option>
            {(isFlat ? topics : children).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>

        {/* Label */}
        <div className="ac-form-group" style={{ margin: 0, minWidth: 160 }}>
          <label style={{ fontSize: "0.78rem" }}>Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Study before class"
          />
        </div>

        <div style={{ display: "flex", gap: "0.4rem", paddingBottom: "2px" }}>
          <button className="ac-btn ac-btn-success ac-btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Add"}
          </button>
          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── Main panel ── */
const StudyMaterialsPanel = ({ curriculumId }) => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCurriculumContent(curriculumId);
      setItems((res.data || []).sort((a, b) => a.sortOrder - b.sortOrder));
    } catch {
      setError("Failed to load study materials.");
    } finally {
      setLoading(false);
    }
  }, [curriculumId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this study material?")) return;
    setError("");
    try {
      await deleteCurriculumContent(id);
      await load();
    } catch {
      setError("Delete failed.");
    }
  };

  const handleMove = async (index, dir) => {
    const newItems = [...items];
    const swapIdx  = index + dir;
    if (swapIdx < 0 || swapIdx >= newItems.length) return;
    [newItems[index], newItems[swapIdx]] = [newItems[swapIdx], newItems[index]];
    setItems(newItems);
    try {
      await reorderCurriculumContent(curriculumId, newItems.map((i) => i.id));
    } catch {
      setError("Reorder failed.");
      load();
    }
  };

  return (
    <div style={{ marginTop: "1.25rem", borderTop: "1px solid #e5e7eb", paddingTop: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#374151" }}>
          📚 Study Materials
          {items.length > 0 && (
            <span style={{ marginLeft: 6, background: "#e0f2fe", color: "#0369a1", borderRadius: 999, padding: "1px 8px", fontSize: "0.72rem", fontWeight: 700 }}>
              {items.length}
            </span>
          )}
        </div>
        {!showForm && (
          <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => setShowForm(true)}>
            + Add Material
          </button>
        )}
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.4rem 0.6rem", borderRadius: 4, marginBottom: "0.6rem", fontSize: "0.82rem" }}>{error}</div>}

      {loading ? (
        <div style={{ fontSize: "0.82rem", color: "#9ca3af" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: "0.82rem", color: "#9ca3af", fontStyle: "italic" }}>No study materials linked yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {items.map((item, idx) => {
            const badge = TYPE_BADGE[item.contentType] || { label: item.contentType, bg: "#f3f4f6", color: "#374151" };
            return (
              <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "0.4rem 0.6rem" }}>
                <span style={{ background: badge.bg, color: badge.color, fontSize: "0.7rem", fontWeight: 700, padding: "2px 7px", borderRadius: 999, flexShrink: 0 }}>
                  {badge.label}
                </span>
                <span style={{ flex: 1, fontSize: "0.85rem", color: "#1f2937", fontWeight: 500 }}>{item.contentTitle}</span>
                {item.label && (
                  <span style={{ fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>{item.label}</span>
                )}
                <div style={{ display: "flex", gap: "0.2rem", flexShrink: 0 }}>
                  <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => handleMove(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                  <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => handleMove(idx, 1)} disabled={idx === items.length - 1} title="Move down">↓</button>
                  <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => handleDelete(item.id)}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <AddMaterialForm
          curriculumId={curriculumId}
          onSaved={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default StudyMaterialsPanel;
